import { useEffect, useMemo, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  RootState,
} from '../../utils/agent.store'
import {
  DataTypeStore,
  EntityLookupSearchCriteria,
  getClickableConfigQueueAPI,
  getLayoutByIdApi,
  getQueueByIdApi,
  hideLoader,
  LookupRelatedEntities,
  setSelectedQueueReducer,
  showLoader,
  SORT_KEYS,
  TableFilterLookupObjectBody,
  TableFilterObjectBody,
  appendTenantIdToPath,
  openWithTenant,
} from '@app-store'
import { DynamicQueuesType } from '../../types/queueConfig.types'
import {
  CustomAgTable,
  FIELD_VALUE_CONSTANTS,
  filterOperatorValues,
  LookupSearchCriteria,
  Pagination,
  TableColumnType,
  TableFilterConfig,
  tableFilterType,
} from '@ui-kit'
import { EntityLayout } from '../../types/layout.types'
import {
  formatDataForListView,
  formatFilterConfig,
  getFilterData,
} from '../../utils/listing.utils'
import {
  convertToCamelCase,
  formatDashboardQueueName,
  formatFaxNumber,
  formatToWord,
} from '../../utils/helperFunctions'
import { componentNames, ENTITY_NAME, NAMESPACES } from '../../utils/constants'
import noop, { isEmpty } from 'lodash'
import { AppComponentProps } from '../../utils/types'
import { useParams, useSearchParams } from 'react-router-dom'
import { FieldValues } from 'react-hook-form'

export const DashboardQueueTable: React.FC<AppComponentProps> = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const caseEntityId = useAppSelector(
    (state: RootState) => state?.clientPrograms?.caseEntityId
  )
  const selectedTenant = useAppSelector(
    (state: RootState) => state?.authUser?.selectedClientTenant
  )

  const [queueName, setQueueName] = useState<string>('')

  const isAgent = useAppSelector(
    (state: RootState) =>
      !state?.clientPrograms?.selectedClientPrograms?.isOverseer
  )

  const { queueId } = useParams()
  const name = searchParams.get('name') ?? ''
  const filter = searchParams.get('filter') ?? ''
  const programId = Number(searchParams.get('programId')) || 0
  const usersIdParam = searchParams.get('users_id')
  const users_id = usersIdParam ? JSON.parse(usersIdParam) : ''
  const [layoutConfig, setLayoutConfig] = useState<{
    entity_id: number
    layout_id: number
  }>({
    entity_id: -1,
    layout_id: -1,
  })
  const [filteredData, setFilteredData] = useState<TableFilterConfig>({
    filter_criteria: [],
    related_entities: {},
  })
  const [queueData, setQueueData] = useState<DynamicQueuesType>()
  const [currentPage, setCurrentPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )

  const [entityData, setEntityData] = useState<DataTypeStore[]>([])
  const [rowPerPage, setRowPerPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.TWENTY_FIVE
  )
  const [listingLayout, setListingLayout] = useState<EntityLayout>()
  const [totalPages, setTotalPages] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [apiFilter, setApiFilter] = useState<
    TableFilterObjectBody | undefined
  >()

  const [entityName, setEntityName] = useState<string>('')

  const getFilters = (entity: FieldValues) => {
    return (entity.filters ?? []).map((filter: EntityLookupSearchCriteria) => ({
      criteria: (filter.criteria ?? [])?.map(
        (criterion: LookupRelatedEntities) => ({
          field_name: criterion.field_name,
          operation: criterion.operation,
          values: criterion.values,
          direction: criterion.direction ?? null,
        })
      ),
    }))
  }
  const fetchQueueById = async (id: number) => {
    try {
      dispatch(showLoader())
      const response = await getQueueByIdApi(id)
      if (response) {
        setQueueData(response?.data)
        setQueueName(response?.data?.name)
        setLayoutConfig({
          entity_id: response?.data?.entity_id,
          layout_id: response?.data?.layout_id,
        })
        setEntityName(response?.data?.entity_name)
        const transformedRelatedEntities = Object.keys(
          response?.data?.related_entities ?? {}
        )?.reduce((acc, key) => {
          const entity = response.data.related_entities[key]
          acc[key] = {
            filters: getFilters(entity),
          }
          return acc
        }, {} as TableFilterLookupObjectBody)
        setFilteredData({
          filter_criteria: response?.data?.filter_criteria,
          related_entities: transformedRelatedEntities,
        })
        dispatch(setSelectedQueueReducer(response?.data?.name))
      }
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchLayoutById = async (entity_id: string, layout_id: string) => {
    try {
      dispatch(showLoader())
      const response = await getLayoutByIdApi({
        entityId: entity_id,
        layoutId: layout_id,
      })
      if (response) {
        setListingLayout(response?.data)
      }
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }
  function getSlaStatusClass(slaStatus: string): string {
    if (slaStatus === NAMESPACES.OVERDUE) {
      return 'bg-red-100 border-red-600'
    } else if (slaStatus === NAMESPACES.AT_RISK) {
      return 'bg-yellow-100 border-yellow-700'
    } else {
      return 'bg-green-100 border-green-700'
    }
  }

  const getTableHeadersFromDashboardLayout = (
    layout: EntityLayout | undefined,
    _entityName = ''
  ): TableColumnType<DataTypeStore>[] => {
    const tableHeader: TableColumnType<DataTypeStore>[] =
      layout?.layout_data?.pages?.[0].rows?.[0].columns?.[0].elements.map(
        (element) => {
          const isCreatedAt = element.keyName === NAMESPACES.CREATED_AT
          const addedField =
            listingLayout?.layout_data?.added_fields?.[element?.keyName ?? '']

          const accessor = element.parent?.lookupKeyName
            ? `${element.parent.lookupKeyName}.${element.keyName}`
            : element.keyName ?? ''

          const baseColumn = {
            header: addedField?.label ?? element.meta?.label ?? '',
            accessor,
            minWidth: '3',
            type: 'text',

            isSortable: true,
            filterData:
              element?.elementName !== componentNames.SINGLE_LOOKUP &&
              element?.elementName !== componentNames.CATEGORY
                ? getFilterData(
                    element,
                    accessor === NAMESPACES.SENDER_NAME,
                    layout?.layout_data?.added_fields?.[element?.keyName ?? '']
                  )
                : {
                    dataType: tableFilterType.STRING_DYNAMIC,
                    convertToUppercase: false,
                  },
            isLookup:
              layout?.layout_data?.added_fields?.[
                element?.keyName ?? ''
              ]?.ui_data_type?.includes(componentNames.CATEGORY) ||
              layout?.layout_data?.added_fields?.[
                element?.keyName ?? ''
              ]?.ui_data_type?.includes(componentNames.SINGLE_LOOKUP) ||
              false,
            LookupConfig: element?.elementName?.includes(
              componentNames.CATEGORY
            )
              ? [NAMESPACES.VALUE]
              : addedField?.selected_fields?.map(
                  (field) => field?.field_name ?? ''
                ) ?? [],
          }

          if (isCreatedAt) {
            return {
              ...baseColumn,
              type: 'component',
              renderComponent: (item, _rowIndex) => {
                return item.sla_status ? (
                  <div
                    className={`text-xs font-light border-l-2 rounded-tr-sm rounded-br-sm p-2 ${getSlaStatusClass(
                      String(item.sla_status)
                    )}`}
                  >
                    {item.created_at}
                  </div>
                ) : (
                  <div className="text-xs font-light">{item.created_at}</div>
                )
              },
            }
          } else {
            return {
              ...baseColumn,
              type: 'text',
              isHidden: false,
            }
          }
        }
      ) || []
    const updatedTableHeader = tableHeader?.map((item) => {
      const isCommunicationLink =
        item?.accessor === NAMESPACES.COMMUNICATION_ID_KEY ||
        item?.accessor === NAMESPACES?.ENROLLMENT_ID_KEY

      const isCaseLink =
        item?.accessor === NAMESPACES.CASE_KEY ||
        item?.accessor === NAMESPACES.CASE_ID_KEY

      if (isCommunicationLink || isCaseLink) {
        return { ...item, type: 'hyperlink' }
      }
      if (
        (item?.accessor === NAMESPACES.USER_NAME_KEY ||
          item?.accessor === NAMESPACES.USERS_KEY) &&
        isAgent
      ) {
        item.isHidden = true
      }

      return item
    })

    return updatedTableHeader ?? []
  }

  const configColumns = useMemo<TableColumnType<DataTypeStore>[]>(() => {
    return listingLayout
      ? getTableHeadersFromDashboardLayout(listingLayout)
      : []
  }, [listingLayout])

  const getProgramIdKey = (): string => {
    if (entityName === 'case' || entityName === 'missing_info_dashboard') {
      return 'program_id'
    } else if (entityName === 'hcp_portal_entity') {
      return 'hcp_program_id'
    }

    return 'programs_id'
  }

  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  }>({
    keyName: 'createdAt',
    direction: SORT_KEYS.Asc,
    isAsc: SORT_KEYS.Asc,
    key: 'created_at',
  })

  const getFiltersValue = (users_id: string | null, entityName: string) => {
    return [
      ...(users_id
        ? [
            {
              operation: filterOperatorValues.EQUALS,
              field_name:
                entityName !== 'case' && entityName !== 'missing_info_dashboard'
                  ? 'users_id'
                  : 'user_id',
              values: [Number(users_id)] as number[],
            },
          ]
        : []),
    ]
  }

  const fetchEntityData = async (rowsPerPage = 10, currentPage = 0) => {
    try {
      dispatch(showLoader())
      const keys = Object.keys(listingLayout?.layout_data?.added_fields ?? {})
      if (!keys?.includes('id')) {
        keys.unshift('id')
      }

      const baseFilterCriteria: {
        operation: string
        field_name: string
        values: string[] | number[]
      }[] = [
        {
          operation: filterOperatorValues.EQUALS,
          field_name: getProgramIdKey(),
          values: [programId ?? 0],
        },
        ...getFiltersValue(users_id, entityName),
      ]
      const formatFilter: {
        filter_criteria: LookupSearchCriteria[]
        related_entities: TableFilterLookupObjectBody
      } = formatFilterConfig(apiFilter ?? {}, configColumns, 'Queue', {
        key: sortDirection?.key,
        direction: sortDirection?.direction?.toUpperCase(),
      }) as {
        filter_criteria: LookupSearchCriteria[]
        related_entities: TableFilterLookupObjectBody
      }

      formatFilter.filter_criteria.push(
        ...(baseFilterCriteria as LookupSearchCriteria[])
      )

      const entity = await getClickableConfigQueueAPI(
        Number(queueId),
        filter ?? '',
        currentPage,
        rowsPerPage,
        formatFilter
      )
      if (entity) {
        const tempData = formatDataForListView(
          entity?.data?.content?.map((item: DataTypeStore) => {
            const formattedCreatedAt = item?.created_at
              ?.toString()
              ?.includes('Z')
              ? item?.created_at
              : item?.created_at + 'Z'
            const formattedModifiedAt = item?.modified_at
              ?.toString()
              ?.includes('Z')
              ? item?.modified_at
              : item?.modified_at + 'Z'
            return {
              ...item,
              created_at: formattedCreatedAt,
              modified_at: formattedModifiedAt,
            }
          }),
          listingLayout?.layout_data,
          true
        )

        const formattedTempData = tempData.map((entry) => ({
          ...entry,
          sender_name: formatFaxNumber(String(entry.sender_name)),
          created_at: entry?.created_at ?? '--',
        }))

        setEntityData(formattedTempData)

        setTotalPages(entity?.data?.total_pages ?? 1)
        if (rowsPerPage) {
          setRowPerPage(rowsPerPage)
        }
      }
    } catch (err) {
      console.error(err)
      setEntityData([])
    } finally {
      dispatch(hideLoader())
    }
  }

  const handlePageChange = async (page: number, rows: number) => {
    try {
      dispatch(showLoader())
      setRowPerPage(rows)
      const currentPage = rows !== rowPerPage ? 1 : page
      setCurrentPage(currentPage)
      await fetchEntityData(rows, currentPage - 1)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
      noop(filteredData)
    }
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      const newKeyName = convertToCamelCase(key)
      setSortDirection({
        keyName: isAsc === SORT_KEYS.Disabled ? '' : newKeyName,
        direction: isAsc,
        key: isAsc === SORT_KEYS.Disabled ? '' : key,
        isAsc: isAsc,
      })
    }
  }

  const onColumnClick = (
    isHeader: boolean,
    accessor: keyof DataTypeStore,
    row?: DataTypeStore | null,
    _rowIndex?: number | null
  ) => {
    if (accessor === 'case_key' || accessor === NAMESPACES.CASE_ID_KEY) {
      openWithTenant(
        appendTenantIdToPath(
          `entity/${caseEntityId}/workflow/review/${row?.id}`,
          selectedTenant?.id
        ),
        '_blank'
      )
    } else if (accessor === 'communication_id') {
      openWithTenant(
        appendTenantIdToPath(
          `manage-queue/dynamic-queue/${layoutConfig.entity_id}/${layoutConfig.layout_id}/${queueId}/${row?.id}/${caseEntityId}`,
          selectedTenant?.id
        ),
        '_blank'
      )
    } else if (accessor === NAMESPACES?.ENROLLMENT_ID_KEY) {
      openWithTenant(
        appendTenantIdToPath(
          `manage-queue/dynamic-queue/${layoutConfig.entity_id}/${layoutConfig.layout_id}/${queueId}/${row?.id}/${caseEntityId}/hcp`,
          selectedTenant?.id
        ),
        '_blank'
      )
    }
  }

  const onFilterChange = (value: TableFilterObjectBody) => {
    setApiFilter(value)
  }

  useEffect(() => {
    if (layoutConfig?.entity_id !== -1 && layoutConfig?.layout_id !== -1) {
      fetchLayoutById(
        (layoutConfig?.entity_id ?? -1)?.toString(),
        (layoutConfig?.layout_id ?? -1)?.toString()
      )
    }
  }, [layoutConfig])

  useEffect(() => {
    fetchQueueById(parseInt(queueId ?? ''))
  }, [queueId])

  useEffect(() => {
    if (listingLayout && configColumns)
      fetchEntityData(rowPerPage, currentPage - 1)
  }, [listingLayout, configColumns, sortDirection, apiFilter])

  return (
    <div className="flex flex-col h-full font-primary">
      <div className="p-3 w-full font-primary font-medium leading-6 text-base border-b-2">
        <div className="ml-1">
          {formatDashboardQueueName(queueName) ?? formatToWord(name)}
        </div>
      </div>
      <div className="h-full overflow-y-auto">
        {listingLayout && (
          <div className="flex flex-col h-full bg-accent-0">
            <div className="flex flex-1 min-h-0">
              <CustomAgTable<DataTypeStore>
                dataQaId="custom-table-manage-queue"
                tableName={`queue-${queueId}-${selectedTenant?.id}`}
                pageName="dashboard-queue-page"
                key={`key-${queueId}-${selectedTenant?.id}`}
                data={entityData}
                isColumnChooser
                actions={[]}
                totalPages={totalPages}
                rowsPerPage={rowPerPage}
                columns={configColumns}
                sort={
                  sortDirection.key && !isEmpty(sortDirection?.key)
                    ? { key: sortDirection?.key, isAsc: sortDirection?.isAsc }
                    : null
                }
                onSortClick={onSortClick}
                showTableColumnOnEmpty
                emptyDataMessage={
                  queueData?.entity_name === ENTITY_NAME.COMMUNICATION
                    ? 'No faxes available for the selected client.'
                    : 'No records found'
                }
                onColumnClick={(
                  isHeader: boolean,
                  accessor: keyof DataTypeStore,
                  row?: DataTypeStore | null,
                  rowIndex?: number | null
                ) => onColumnClick(isHeader, accessor, row, rowIndex)}
                filterObject={apiFilter}
                onFilterChange={onFilterChange}
              />
            </div>
            <div>
              <Pagination
                currentPage={currentPage}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                rowsPer={rowPerPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
